import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { getMe, setAccessToken, updateProfile } from "@/lib/api";
import { queryClient } from "@/lib/cache/client";

const AuthContext = createContext(null);

function mergeUserFromSession(session, profile = null) {
  if (!session?.user) return null;
  const meta = session.user.user_metadata || {};
  const base = {
    user_id: session.user.id,
    email: session.user.email || "",
    name: meta.name || meta.full_name || session.user.email?.split("@")[0] || "",
    picture: meta.picture || meta.avatar_url || "",
    operating_country: meta.operating_country || "JO",
  };
  if (profile) {
    return {
      ...base,
      name: profile.name || base.name,
      email: profile.email || base.email,
      picture: profile.picture || base.picture,
      operating_country: profile.operating_country || base.operating_country,
    };
  }
  return base;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async (session) => {
    if (!session?.user) {
      setAccessToken(null);
      queryClient.clear();
      setUser(false);
      return;
    }
    setAccessToken(session.access_token);
    setUser(mergeUserFromSession(session));
    try {
      const profile = await getMe();
      setUser(mergeUserFromSession(session, profile));
    } catch {
      /* keep session-derived user */
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      refreshUser(data.session).finally(() => {
        if (mounted) setLoading(false);
      });
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      refreshUser(session).finally(() => setLoading(false));
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [refreshUser]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await refreshUser(data.session);
    return data.user;
  }, [refreshUser]);

  const signUp = useCallback(async (email, password, name, operatingCountry = "JO") => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, operating_country: operatingCountry } },
    });
    if (error) throw error;
    if (data.session) {
      setAccessToken(data.session.access_token);
      try {
        await updateProfile({ operating_country: operatingCountry });
      } catch {
        /* profile may not exist yet */
      }
      await refreshUser(data.session);
    }
    return data;
  }, [refreshUser]);

  const signInWithGoogle = useCallback(async () => {
    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) throw error;
  }, []);

  const updateOperatingCountry = useCallback(async (code) => {
    const profile = await updateProfile({ operating_country: code });
    setUser((prev) => (prev && typeof prev === "object" ? { ...prev, ...profile } : prev));
    return profile;
  }, []);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    queryClient.clear();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signInWithGoogle,
        logout,
        refreshUser,
        updateOperatingCountry,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
